import { useNavigate } from '@tanstack/react-router';
import axios from 'axios';
import { Form, Formik } from 'formik';

import type { VesselMessage } from '../../../../../types/VesselMessage';
import { VesselMigrationDataType } from '../../../../../types/VesselMigrationMessage';
import { Button } from '../../../../cdl/Button/Button';
import { Checkbox } from '../../../../cdl/Checkbox/Checkbox';
import { Modal } from '../../../../cdl/Modal/Modal';
import { Text } from '../../../../cdl/Text/Text';
import { useToasts } from '../../../../cdl/Toast/useToasts';
import { translate } from '../../../../common/helpers/translate.helper';
import { Box } from '../../../../common/ui/Box';
import { VesselSelect } from '../../../VesselSelect';

import { TargetVesselSelect } from './TargetVesselSelect';
import { useVesselDataMigration } from './useVesselDataMigration';

interface VesselDataMigrationFormValues {
    sourceVessel: VesselMessage | null;
    targetVesselId: string | null;
    dataTypes: VesselMigrationDataType[];
}

const initialValues: VesselDataMigrationFormValues = {
    sourceVessel: null,
    targetVesselId: null,
    dataTypes: Object.values(VesselMigrationDataType),
};

const getDataTypeLabel = (dataType: VesselMigrationDataType) => {
    switch (dataType) {
        case VesselMigrationDataType.TANKS:
            return translate('vessel.migration.dataTypes.tanks');
        case VesselMigrationDataType.STOCKS:
            return translate('vessel.migration.dataTypes.stocks');
        case VesselMigrationDataType.CONSUMPTION_PRESETS:
            return translate('vessel.migration.dataTypes.consumptionPresets');
    }
};

const toggleDataType = (
    dataTypes: VesselMigrationDataType[],
    dataType: VesselMigrationDataType,
    checked: boolean
): VesselMigrationDataType[] => {
    if (checked) {
        return [...dataTypes, dataType];
    }

    const dependentDataTypes =
        dataType === VesselMigrationDataType.TANKS ? [dataType, VesselMigrationDataType.STOCKS] : [dataType];
    return dataTypes.filter((it) => !dependentDataTypes.includes(it));
};

interface VesselDataMigrationModalProps {
    onDismiss: () => void;
}

export const VesselDataMigrationModal = ({ onDismiss }: VesselDataMigrationModalProps) => {
    const navigate = useNavigate();
    const { addToast, addErrorToast } = useToasts();
    const vesselDataMigration = useVesselDataMigration();

    const handleSubmit = async ({ sourceVessel, targetVesselId, dataTypes }: VesselDataMigrationFormValues) => {
        if (!sourceVessel || !targetVesselId) {
            return;
        }

        try {
            const result = await vesselDataMigration.mutateAsync({
                sourceVesselId: sourceVessel.id,
                request: {
                    targetVesselId,
                    dataTypes,
                },
            });

            addToast(
                translate('vessel.migration.success', {
                    tanks: result.copiedTanks.toString(),
                    stocks: result.copiedStocks.toString(),
                    presets: result.copiedConsumptionPresets.toString(),
                })
            );
            onDismiss();
            navigate({ to: '/vessel/$id', params: { id: result.targetVesselId } });
        } catch (error) {
            const message = axios.isAxiosError(error) ? error.response?.data?.message : undefined;
            addErrorToast(message || translate('vessel.migration.error'));
        }
    };

    return (
        <Modal isOpen onDismiss={onDismiss} dismissible={!vesselDataMigration.isPending}>
            <Formik initialValues={initialValues} onSubmit={handleSubmit}>
                {({ values, setFieldValue, isSubmitting }) => (
                    <Form>
                        <Box display="grid" gap={16} padding={24}>
                            <Text variant="title">{translate('vessel.migration.headline')}</Text>
                            <Text variant="small" color="foreground.muted">
                                {translate('vessel.migration.description')}
                            </Text>

                            <VesselSelect
                                label={translate('vessel.migration.sourceVessel')}
                                caption={translate('vessel.migration.sourceVesselCaption')}
                                vesselId={values.sourceVessel?.id ?? null}
                                active={false}
                                onChange={(vessel) => {
                                    setFieldValue('sourceVessel', vessel);
                                    setFieldValue('targetVesselId', null);
                                }}
                            />

                            {values.sourceVessel ? (
                                <TargetVesselSelect key={values.sourceVessel.id} sourceVessel={values.sourceVessel} />
                            ) : (
                                <VesselSelect
                                    label={translate('vessel.migration.targetVessel')}
                                    caption={translate('vessel.migration.targetVesselCaption')}
                                    vesselId={null}
                                    disabled
                                />
                            )}

                            <Box display="grid" gap={8}>
                                <Text variant="fieldLabel">{translate('vessel.migration.dataTypesLabel')}</Text>
                                {Object.values(VesselMigrationDataType).map((dataType) => (
                                    <Checkbox
                                        key={dataType}
                                        label={getDataTypeLabel(dataType)}
                                        checked={values.dataTypes.includes(dataType)}
                                        disabled={
                                            dataType === VesselMigrationDataType.STOCKS &&
                                            !values.dataTypes.includes(VesselMigrationDataType.TANKS)
                                        }
                                        onChange={(event) =>
                                            setFieldValue(
                                                'dataTypes',
                                                toggleDataType(values.dataTypes, dataType, event.target.checked)
                                            )
                                        }
                                    />
                                ))}
                                <Text variant="small" color="foreground.muted">
                                    {translate('vessel.migration.schedulesHint')}
                                </Text>
                            </Box>

                            <Box display="flex" justifyContent="end" gap={8}>
                                <Button type="button" onClick={onDismiss} disabled={isSubmitting}>
                                    {translate('btn.cancel')}
                                </Button>
                                <Button
                                    emphasis="high"
                                    type="submit"
                                    disabled={
                                        !values.sourceVessel || !values.targetVesselId || !values.dataTypes.length
                                    }
                                    isLoading={isSubmitting}
                                >
                                    {translate('vessel.migration.submit')}
                                </Button>
                            </Box>
                        </Box>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};
